import { createTheme, alpha } from '@mui/material/styles'
import type { Shadows } from '@mui/material/styles'
import { heIL as coreHeIL } from '@mui/material/locale'
import { heIL as gridHeIL } from '@mui/x-data-grid/locales'
import type {} from '@mui/x-data-grid/themeAugmentation'

/**
 * Navy carries structure, brass marks what is active or derived. Brass is never used
 * for body text — at 14px on white it fails contrast, which is why the old subtitles
 * read as washed out.
 */
const navy = '#1e3a5f'
const brass = '#b08d57'
const ink = '#16202e'
const muted = '#5a6675'
const line = '#e3e7ec'

export const SIDEBAR_WIDTH = 232
export const CONTENT_MAX_WIDTH = 1440

export const theme = createTheme(
  {
    direction: 'rtl',
    palette: {
      mode: 'light',
      primary: { main: navy, light: '#35577f', dark: '#14283f', contrastText: '#ffffff' },
      secondary: { main: brass, light: '#c5a678', dark: '#8d6f42', contrastText: '#ffffff' },
      background: { default: '#f4f5f7', paper: '#ffffff' },
      text: { primary: ink, secondary: muted, disabled: '#9aa4b0' },
      divider: line,
      success: { main: '#2e7d32' },
      warning: { main: '#c77700' },
      error: { main: '#c62828' },
      info: { main: navy },
      action: { hover: alpha(navy, 0.04), selected: alpha(navy, 0.07) },
    },

    typography: {
      fontFamily: ['Heebo', 'Segoe UI', 'Arial', 'sans-serif'].join(','),
      h4: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.25 },
      h5: { fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.3 },
      h6: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.35 },
      subtitle1: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
      subtitle2: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.4 },
      body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
      body2: { fontSize: '0.875rem', lineHeight: 1.55 },
      caption: { fontSize: '0.75rem', lineHeight: 1.5 },
      button: { textTransform: 'none', fontWeight: 600 },
    },

    shape: { borderRadius: 10 },

    // Flatter than MUI's defaults: surfaces are separated by hairlines, and only
    // things that genuinely float (menus, dialogs) keep a real shadow.
    shadows: [
      'none',
      '0 1px 2px rgba(22,32,46,0.06)',
      '0 2px 8px rgba(22,32,46,0.07)',
      '0 4px 14px rgba(22,32,46,0.08)',
      ...createTheme({}).shadows.slice(4),
    ] as Shadows,

    components: {
      // Deliberately no global ::-webkit-scrollbar styling: a universal selector also
      // overrides the DataGrid's own hiding of its native scrollbars, which then show
      // up alongside the ones it draws itself.

      MuiTextField: { defaultProps: { size: 'small', fullWidth: true } },
      MuiSelect: { defaultProps: { size: 'small' } },
      MuiFormControl: { defaultProps: { size: 'small' } },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8, paddingInline: 16 },
          sizeLarge: { paddingBlock: 10 },
          outlined: { borderColor: line, '&:hover': { borderColor: navy } },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: line },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 6, height: 24, fontWeight: 500, fontSize: '0.75rem' },
          label: { paddingInline: 8 },
          outlined: { borderColor: line },
        },
      },

      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 44, borderBottom: `1px solid ${line}` },
          indicator: { height: 3, borderRadius: '3px 3px 0 0' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 44,
            fontWeight: 500,
            color: muted,
            '&.Mui-selected': { fontWeight: 600 },
          },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            marginInline: 8,
            paddingBlock: 7,
            position: 'relative',
            '&.Mui-selected': {
              backgroundColor: alpha(navy, 0.08),
              '&:hover': { backgroundColor: alpha(navy, 0.11) },
              // Brass rail on the inline-start edge, so the active row is legible at a
              // glance. Logical property: the RTL plugin must not flip it.
              '&::before': {
                content: '""',
                position: 'absolute',
                insetInlineStart: 0,
                insetBlock: 6,
                width: 3,
                borderRadius: 3,
                backgroundColor: brass,
              },
              '& .MuiListItemIcon-root': { color: navy },
              '& .MuiListItemText-primary': { color: navy, fontWeight: 600 },
            },
          },
        },
      },
      MuiListItemIcon: { styleOverrides: { root: { color: muted, minWidth: 34 } } },
      MuiListItemText: { styleOverrides: { primary: { fontSize: '0.9375rem' } } },

      MuiTableCell: {
        styleOverrides: {
          root: { textAlign: 'right', borderColor: line },
          head: { fontWeight: 600, backgroundColor: '#fafbfc', color: muted },
        },
      },

      MuiDialog: { styleOverrides: { paper: { borderRadius: 14 } } },
      MuiDialogTitle: { styleOverrides: { root: { fontSize: '1.125rem', fontWeight: 600 } } },
      MuiTooltip: { styleOverrides: { tooltip: { fontSize: '0.75rem', borderRadius: 6 } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 10 }, message: { paddingBlock: 6 } } },
      MuiDivider: { styleOverrides: { root: { borderColor: line } } },

      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: 'none',
            '--DataGrid-rowBorderColor': line,
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none',
            },
          },
          columnHeaders: { borderBottom: `1px solid ${line}` },
          columnHeader: { backgroundColor: '#fafbfc' },
          columnHeaderTitle: { fontWeight: 600, color: muted, fontSize: '0.8125rem' },
          row: { '&:hover': { backgroundColor: alpha(navy, 0.03) } },
          cell: { fontSize: '0.875rem' },
          footerContainer: { borderTop: `1px solid ${line}` },
        },
      },
    },
  },
  coreHeIL,
  gridHeIL
)
